<template>
  <!-- CRM read-only replica of the buyer step: each box renders its ordered elements
       in the active language. Pure presentation — inputs are disabled. -->
  <div class="step-questions">
    <h2 class="step-title">{{ titleText }}</h2>
    <p class="step-subtitle">{{ subtitleText }}</p>

    <p v-if="!boxes.length" class="empty">{{ emptyText }}</p>

    <div v-for="(box, bi) in boxes" :key="bi" class="question-group">
      <p v-if="!box.elements.length" class="el-empty">{{ boxEmptyText }}</p>
      <div v-for="(el, ei) in box.elements" :key="ei" class="el-item">
        <h3 v-if="el.type === 'title'" class="el-title">{{ el.text || placeholderTitle }}</h3>

        <div v-else-if="el.type === 'textarea'" class="el-body" v-html="el.text || placeholderBody"></div>

        <div v-else-if="el.type === 'input'" class="el-input" :class="{ 'el-input--money': el.inputType === 'money' }">
          <label class="el-label">{{ el.label || placeholderInput }}<span v-if="el.required" class="req">*</span></label>
          <div v-if="el.inputType === 'money'" class="money-line">
            <input class="money-input" type="number" disabled />
            <span class="cur">{{ currencySymbol }}</span>
          </div>
          <input v-else class="form-input" type="text" disabled />
        </div>

        <div v-else-if="el.type === 'single' || el.type === 'multiple'" class="el-choices">
          <label v-for="(opt, oi) in el.options" :key="oi" class="choice-line">
            <input :type="el.type === 'multiple' ? 'checkbox' : 'radio'" :name="'b' + bi + '_' + ei" disabled />
            <span>{{ opt.label || opt.value }}</span>
          </label>
          <p v-if="!el.options.length" class="choice-empty">{{ noChoicesText }}</p>
        </div>

        <label v-else-if="el.type === 'checkbox'" class="checkbox-line">
          <input type="checkbox" disabled />
          <span>{{ el.label || ackText }}<span v-if="el.required" class="req">*</span></span>
        </label>
      </div>
    </div>

    <div class="step-actions">
      <button class="checkout-btn checkout-btn--secondary" disabled>{{ backText }}</button>
      <button class="checkout-btn checkout-btn--primary" :style="{ backgroundColor: brandColor }" disabled>{{ nextText }}</button>
    </div>
  </div>
</template>

<script>
/**
 * Read-only preview of the profile's boxes (composable elements) as the buyer would
 * see them, resolved to the active language (fallback to en). Mirrors the client
 * StepCustomQuestions render.
 */
import previewLang from './previewLang';

export default {
  name: 'QuestionsPreview',
  mixins: [previewLang],
  props: {
    // Editor-shape boxes: { elements:[{ type, labels:{lang}, inputType, required,
    //   includeInSummary, options:[{value,labels:{lang}}] }] }
    questions: { type: Array, default: () => [] },
    text: { type: Object, default: () => ({}) },   // texts[lang] (for the editable heading)
    lang: { type: String, default: 'de' },
    brandColor: { type: String, default: '#1a73e8' }
  },
  computed: {
    boxes() {
      const pick = (m) => (m && (m[this.lang] || m.en)) || '';
      return (this.questions || []).map(box => ({
        elements: (box.elements || []).map(el => {
          const base = { type: el.type, required: !!el.required };
          if (el.type === 'single' || el.type === 'multiple') {
            return { ...base, options: (el.options || []).map(o => ({ value: o.value || '', label: pick(o.labels) || o.value || '' })) };
          }
          if (el.type === 'input') {
            return { ...base, inputType: el.inputType || 'text', label: pick(el.labels) };
          }
          // title / textarea / checkbox all carry a single localized text
          return { ...base, text: pick(el.labels), label: pick(el.labels) };
        })
      }));
    },
    currencySymbol() { return this.t('checkout_currency_symbol', '€'); },
    titleText() {
      return (this.text && this.text.checkout_custom_questions_title)
        || this.t('checkout_custom_questions_title', 'Questions & Remarks');
    },
    subtitleText() {
      return (this.text && this.text.checkout_custom_questions_subtitle)
        || this.t('checkout_custom_questions_subtitle', 'Please complete the following.');
    },
    emptyText() { return this.t('checkout_preview_no_boxes', 'No boxes configured.'); },
    boxEmptyText() { return this.t('checkout_preview_empty_box', '(empty box)'); },
    placeholderTitle() { return this.t('checkout_preview_title_ph', '(title)'); },
    placeholderBody() { return this.t('checkout_preview_text_ph', '(text)'); },
    placeholderInput() { return this.t('checkout_preview_input_ph', '(input)'); },
    ackText() { return this.t('checkout_preview_acknowledge', 'I acknowledge'); },
    noChoicesText() { return this.t('checkout_preview_no_choices', '(no choices added yet)'); },
    backText() { return this.t('checkout_back', 'Back'); },
    nextText() { return this.t('checkout_next', 'Next'); }
  }
};
</script>

<style scoped>
.step-title { font-size: 20px; font-weight: 700; margin: 0 0 6px; }
.step-subtitle { font-size: 13px; color: #666; margin: 0 0 24px; }
.empty { font-style: italic; color: #999; padding: 1rem 0; }

.question-group { border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin-bottom: 16px; }
.el-item { margin-bottom: 14px; }
.el-item:last-child { margin-bottom: 0; }
.el-empty { font-style: italic; color: #bbb; margin: 0; font-size: 12px; }

.el-title { font-size: 16px; font-weight: 700; margin: 0 0 4px; color: #222; }
.el-body { font-size: 13px; color: #444; line-height: 1.5; }
.el-body :deep(p) { margin: 0 0 6px; }
.el-label { font-size: 12px; font-weight: 600; color: #555; display: block; margin-bottom: 3px; }
.req { color: #e74c3c; }

/* Money: label left, field right (matches buyer view). */
.el-input--money { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.el-input--money .el-label { margin-bottom: 0; flex: 1; min-width: 0; }
.el-input--money .money-line { flex-shrink: 0; }

.form-input { padding: 8px 12px; border: 1px solid #d0d0d0; border-radius: 4px; font-size: 14px; width: 100%; box-sizing: border-box; }
.money-line { display: inline-flex; align-items: baseline; gap: 6px; max-width: 160px; border-bottom: 1px solid #c8c8c8; }
.money-input { width: 110px; border: none; background: transparent; padding: 6px 0; font-size: 14px; text-align: right; }
.money-input::-webkit-outer-spin-button, .money-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.cur { font-size: 14px; font-weight: 600; color: #555; }

.el-choices { display: flex; flex-direction: column; gap: 8px; margin-top: 2px; }
.choice-line { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #333; }
.choice-line input { width: auto; }
.choice-empty { font-size: 12px; color: #aaa; font-style: italic; margin: 0; }

.checkbox-line { display: flex; align-items: center; gap: 8px; font-size: 14px; }
.checkbox-line input { width: auto; }

.step-actions { margin-top: 28px; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.checkout-btn { padding: 10px 28px; border: none; border-radius: 4px; font-size: 14px; font-weight: 600; }
.checkout-btn--primary { color: #fff; }
.checkout-btn--secondary { background: #f0f0f0; color: #333; }
</style>
